import { isOpenFindingStatus, isOpenSuggestionStatus } from "@repo/shared/reviewCounts";
import { Link, createFileRoute, useRouterState } from "@tanstack/react-router";
import {
  ActivityIcon,
  ArrowLeftIcon,
  HistoryIcon,
  Link2Icon,
  MessageSquareIcon,
  RadarIcon,
  RefreshCwIcon,
  SlidersHorizontalIcon,
  SparklesIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useReducer, useState } from "react";

import { RegulationPageShell } from "../components/regulation/RegulationPageShell";
import { PolicyFileActions } from "../components/regulation/policy/PolicyFileActions";
import {
  FRESHNESS_LABELS,
  FRESHNESS_PILL,
  policyFreshness,
} from "../components/regulation/policy/policyFreshness";
import { PersonAvatars } from "../components/regulation/PersonAvatars";
import { PolicyFindingsTab } from "../components/regulation/policy/PolicyFindingsTab";
import { RecordUpdatesStrip } from "../components/regulation/recordUpdates/RecordUpdatesPage";
import { isGapSuggestionKind, isRecordUpdateKind } from "@repo/shared/findingPlanning";
import { policyPageView, policyTabHash } from "../components/regulation/policy/policyPageTabs";
import { PolicyDocumentPreview } from "../components/regulation/policy/PolicyDocumentPreview";
import { PolicyOverviewTab } from "../components/regulation/policy/PolicyOverviewTab";
import { PolicyVersionsTab } from "../components/regulation/policy/PolicyVersionsTab";
import {
  policyReassessmentExpiresAt,
  policyReassessmentState,
} from "../components/regulation/policy/PolicyReassessment.logic";
import { PolicyReassessmentBanner } from "../components/regulation/policy/PolicyReassessmentBanner";
import {
  FREQUENCY_LABELS,
  PolicyActivityTab,
  PolicyCommentsTab,
  PolicyMappingsTab,
  formatDate,
} from "../components/regulation/policy/PolicyGovernanceTabs";
import { useRegulationOrg } from "../components/regulation/useRegulationOrg";
import {
  AnimatedTabs,
  AnimatedTabsContent,
  AnimatedTabsList,
  AnimatedTabsTrigger,
} from "../components/ui/animated-tabs";
import {
  policyDisplayName,
  policyFileVersions,
  policyVersion,
  usePolicies,
  useRegulatoryUpdates,
} from "../state/policies";
import {
  usePolicyActivity,
  usePolicyFindings,
  usePolicyOwners,
  useRecommendations,
  usePolicyGovernance,
  usePolicyMappings,
} from "../state/governance";
import { useReassessments } from "../state/inspections";
import { cn } from "~/lib/utils";
import { Skeleton } from "../components/ui/skeleton";

/** A zero badge is width without information; the count appears once there is one. */
function TabCount({ value }: { readonly value: number | null }) {
  if (value === null || value === 0) return null;
  return (
    <span className="rounded-full bg-muted px-1.5 py-px text-[11px] font-medium tabular-nums text-foreground/70">
      {value}
    </span>
  );
}

/**
 * One fact in the header strip. Wide, the facts run in a line; in a narrow
 * header (see `RegulationPageShell`'s `page-header` container) the item
 * dissolves into the strip's two-column grid, labels in one column and
 * values in the other.
 */
function MetaItem({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <span className="contents text-sm @[44rem]/page-header:flex @[44rem]/page-header:items-baseline @[44rem]/page-header:gap-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 text-foreground">{children}</span>
    </span>
  );
}

/** Tab icons give way to the labels on a narrow page. */
const TAB_ICON = "hidden size-4 shrink-0 @[30rem]/policy:block";

/**
 * One policy: the governance facts an auditor asks about, in tabs, and the
 * document itself behind the header's View document — a preview over the
 * page, so no PDF loads until someone opens it.
 */
function PolicyDetailRoute() {
  const { org: orgSegment, policyId } = Route.useParams();
  const hash = useRouterState({ select: (state) => state.location.hash });
  const navigate = Route.useNavigate();
  const setTab = (value: unknown) => {
    void navigate({
      hash: policyTabHash(String(value)),
      replace: true,
      resetScroll: false,
      hashScrollIntoView: false,
    });
  };
  const { org } = useRegulationOrg();
  const organizationId = org?.id ?? null;

  const { snapshot } = usePolicies(organizationId);
  const { snapshot: updates } = useRegulatoryUpdates(organizationId);
  const { snapshot: governance } = usePolicyGovernance(organizationId, policyId);
  const { snapshot: mappings } = usePolicyMappings(organizationId, policyId);
  const { snapshot: activity } = usePolicyActivity(organizationId, policyId);
  const { snapshot: findingList, unavailableReason: findingsUnavailable } = usePolicyFindings(
    organizationId,
    policyId,
  );
  const { snapshot: ownerList } = usePolicyOwners(organizationId, policyId);
  const owners = ownerList?.people ?? [];
  // Findings are every gap on the document: regulatory findings and the gap
  // suggestions (control amendments). The rest of the suggestions are record
  // updates, with their own tab and count.
  const { snapshot: recommendationList, unavailableReason: recommendationsUnavailable } = useRecommendations(
    organizationId,
    policyId,
  );
  const waitingSuggestions =
    recommendationList?.recommendations.filter(
      (item) => item.policyId === policyId && isOpenSuggestionStatus(item.status),
    ) ?? null;
  const openFindings =
    findingList && waitingSuggestions
      ? findingList.findings.filter((finding) => isOpenFindingStatus(finding.status)).length +
        waitingSuggestions.filter((item) => isGapSuggestionKind(item.kind)).length
      : null;
  const openSuggestions = waitingSuggestions ? waitingSuggestions.filter((item) => isRecordUpdateKind(item.kind)).length : null;
  // Every record update on the document, decided ones included: the tab's empty state.
  const recordUpdateCount =
    recommendationList?.recommendations.filter((item) => item.policyId === policyId && isRecordUpdateKind(item.kind)).length ?? null;

  // The hash is the selected tab, both ways: a link back from a finding or a
  // suggestion opens its tab, and a switch writes it back (replacing, so tabs
  // do not pile up in history) for the browser's Back button to return to.
  // `#document` (or the old `#content` tab) opens the document over Overview.
  const { tab, documentRequested, showRecordUpdates } = policyPageView(hash, recordUpdateCount);
  const [documentOpened, setDocumentOpened] = useState(false);
  const documentOpen = documentOpened || documentRequested;
  const setDocumentOpen = (open: boolean) => {
    setDocumentOpened(open);
    // Closing a linked preview drops the hash, so it does not reopen on reload.
    if (!open && documentRequested) setTab("overview");
  };

  // A re-assessment of this document: pushed on the compliance cursor as it
  // starts, runs and settles, so the page says which change is being re-read
  // without anyone refreshing.
  const { snapshot: reassessments } = useReassessments(organizationId, policyId);
  const reassessment = policyReassessmentState(reassessments?.runs ?? [], policyId, Date.now());
  // A failure banner lapses after a day with nothing pushed, so the page
  // re-renders itself at that moment rather than showing it indefinitely.
  const [, lapse] = useReducer((n: number) => n + 1, 0);
  const expiresAt = policyReassessmentExpiresAt(reassessment);
  useEffect(() => {
    if (expiresAt === null) return;
    const timer = setTimeout(lapse, Math.max(0, expiresAt - Date.now()) + 1_000);
    return () => clearTimeout(timer);
  }, [expiresAt]);
  const reassessing = reassessment !== null && reassessment.kind !== "failed";

  const policy = snapshot?.policies.find((candidate) => candidate.id === policyId) ?? null;
  const title = governance?.title ?? (policy ? policyDisplayName(policy) : "Policy");

  const counts = {
    controls: mappings?.mappings.length ?? null,
    // One version is not a history; the count appears once a file was replaced.
    versions: policy === null ? null : policyFileVersions(policy).length === 0 ? 0 : policyVersion(policy),
    comments:
      activity?.activity.filter((entry) => entry.kind === "comment").length ?? null,
  };
  const activityCount =
    activity?.activity.filter((entry) => entry.kind !== "comment").length ?? null;

  const overdue =
    governance?.reviewDueAt != null && Date.parse(governance.reviewDueAt) < Date.now();
  // A regulatory change that names this policy makes it out of date until
  // someone amends it; a lapsed review is the same signal from the calendar.
  const touchedByChange =
    updates?.updates.some((update) => update.affectedPolicyIds.includes(policyId)) ?? false;
  const freshness = policy
    ? // An open finding means the document is not up to date, the same rule the
      // library's status column reads.
      policyFreshness({ policy, governance, touchedByChange, openFindings: openFindings ?? 0, now: Date.now() })
    : "processing";
  // An open finding outranks "up to date" and "not yet checked", so neither
  // may show until the findings have loaded; the stronger states need no wait.
  // A read that came back unavailable will not load, so it settles the wait.
  const findingsSettled =
    openFindings !== null || findingsUnavailable !== null || recommendationsUnavailable !== null;
  const freshnessKnown = findingsSettled || (freshness !== "current" && freshness !== "not-governed");

  return (
    <RegulationPageShell
      eyebrow={
        <Link
          to="/$org/compliance/policies"
          params={{ org: orgSegment }}
          className="inline-flex w-fit cursor-pointer items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="size-3.5" />
          All policies
        </Link>
      }
      title={title}
      titleAside={
        freshnessKnown ? (
          <span
            className={`shrink-0 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${FRESHNESS_PILL[freshness]}`}
          >
            {FRESHNESS_LABELS[freshness]}
          </span>
        ) : (
          <Skeleton aria-label="Checking status" className="h-5 w-20 shrink-0 rounded-full" />
        )
      }
      description={governance?.summary ?? undefined}
      meta={
        <div className="grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-4 gap-y-1.5 @[44rem]/page-header:flex @[44rem]/page-header:flex-wrap @[44rem]/page-header:items-center @[44rem]/page-header:gap-x-5 @[44rem]/page-header:gap-y-1.5">
          <MetaItem label="Owner">
            {/* The set, not the single governance column: a header that says
                "Unassigned" while the page names two owners is a lie. */}
            {owners.length > 0 ? (
              <span className="flex min-w-0 items-center gap-2">
                <PersonAvatars people={owners} max={3} />
                <span className="min-w-0 truncate">
                  {owners[0]?.name ?? owners[0]?.email}
                  {owners.length > 1 ? ` +${owners.length - 1}` : ""}
                </span>
              </span>
            ) : (
              <span className="text-muted-foreground">Unassigned</span>
            )}
          </MetaItem>
          <MetaItem label="Review">
            {governance && governance.reviewFrequency !== "none" ? (
              <>
                {FREQUENCY_LABELS[governance.reviewFrequency]}
                {governance.reviewDueAt ? (
                  <span className={overdue ? "text-destructive" : "text-muted-foreground"}>
                    {" "}
                    · {overdue ? "overdue" : "due"} {formatDate(governance.reviewDueAt)}
                  </span>
                ) : null}
              </>
            ) : (
              <span className="text-muted-foreground">No review cycle</span>
            )}
          </MetaItem>
          {policy ? <MetaItem label="Uploaded">{formatDate(policy.createdAt)}</MetaItem> : null}
        </div>
      }
      actions={
        policy ? (
          <PolicyFileActions
            organizationId={organizationId}
            policy={policy}
            onViewDocument={() => setDocumentOpen(true)}
          />
        ) : null
      }
    >
      {() => (
        <>
        <PolicyDocumentPreview
          organizationId={organizationId}
          policyId={policyId}
          open={documentOpen}
          onOpenChange={setDocumentOpen}
        />
        {reassessment !== null ? (
          <PolicyReassessmentBanner state={reassessment} orgSegment={orgSegment} />
        ) : null}
        {/* `policy` is the container the tab strip and the tab bodies query. */}
        <AnimatedTabs value={tab} onValueChange={setTab} className="mt-6 @container/policy">
          <AnimatedTabsList>
            <AnimatedTabsTrigger value="overview">
              <SlidersHorizontalIcon className={TAB_ICON} />
              Overview
            </AnimatedTabsTrigger>
            <AnimatedTabsTrigger value="mappings">
              <Link2Icon className={TAB_ICON} />
              Controls
              <TabCount value={counts.controls} />
            </AnimatedTabsTrigger>
            <AnimatedTabsTrigger value="findings">
              {reassessing ? (
                <RefreshCwIcon
                  aria-label="Re-assessing"
                  className={cn(
                    "size-4 text-sky-700 dark:text-sky-300",
                    reassessment?.kind === "running" && "animate-spin motion-reduce:animate-none",
                  )}
                />
              ) : (
                <RadarIcon className={TAB_ICON} />
              )}
              Findings
              <TabCount value={openFindings} />
            </AnimatedTabsTrigger>
            {showRecordUpdates ? (
              <AnimatedTabsTrigger value="suggestions">
                <SparklesIcon className={TAB_ICON} />
                Suggestions
                <TabCount value={openSuggestions} />
              </AnimatedTabsTrigger>
            ) : null}
            <AnimatedTabsTrigger value="versions">
              <HistoryIcon className={TAB_ICON} />
              Versions
              <TabCount value={counts.versions} />
            </AnimatedTabsTrigger>
            <AnimatedTabsTrigger value="activity">
              <ActivityIcon className={TAB_ICON} />
              Activity
              <TabCount value={activityCount} />
            </AnimatedTabsTrigger>
            <AnimatedTabsTrigger value="comments">
              <MessageSquareIcon className={TAB_ICON} />
              Comments
              <TabCount value={counts.comments} />
            </AnimatedTabsTrigger>
          </AnimatedTabsList>

          <AnimatedTabsContent value="overview" className="pt-4">
            <PolicyOverviewTab
              organizationId={organizationId}
              policyId={policyId}
              policy={policy}
              governance={governance}
              counts={counts}
            />
          </AnimatedTabsContent>
          <AnimatedTabsContent value="findings" className="pt-4">
            <PolicyFindingsTab
              organizationId={organizationId}
              orgSegment={orgSegment}
              policyId={policyId}
              reassessing={reassessing}
            />
          </AnimatedTabsContent>
          {showRecordUpdates ? (
            <AnimatedTabsContent value="suggestions" className="pt-4">
              <RecordUpdatesStrip organizationId={organizationId} policyId={policyId} />
            </AnimatedTabsContent>
          ) : null}
          <AnimatedTabsContent value="versions" className="pt-4">
            <PolicyVersionsTab
              organizationId={organizationId}
              orgSegment={orgSegment}
              policyId={policyId}
            />
          </AnimatedTabsContent>
          <AnimatedTabsContent value="mappings" className="pt-4">
            <PolicyMappingsTab organizationId={organizationId} policyId={policyId} />
          </AnimatedTabsContent>
          <AnimatedTabsContent value="activity" className="pt-4">
            <PolicyActivityTab organizationId={organizationId} policyId={policyId} />
          </AnimatedTabsContent>
          <AnimatedTabsContent value="comments" className="pt-4">
            <PolicyCommentsTab organizationId={organizationId} policyId={policyId} />
          </AnimatedTabsContent>
        </AnimatedTabs>
        </>
      )}
    </RegulationPageShell>
  );
}

export const Route = createFileRoute("/$org/compliance/policies/$policyId")({
  component: PolicyDetailRoute,
});
